import React, { useEffect, useRef, useState } from 'react'
import * as S from './style'
import SearchInput from './search'
import Head from './head'
import { ActionProps, TableHeaderProps } from './types'
import useDebounce from '@hooks/useDebounce'
import Pagination from '@components/pagination'
import Body from './body'
import jsPDF from 'jspdf'
import html2canvas from 'html2canvas'

export interface TableProps {
  title?: string
  columns?: TableHeaderProps[]
  data?: any[]
  searchKeys?: string[]
  totalPages?: number
  totalItems?: number
  itemsPerPage?: number
  hasExportButton?: boolean
  hasText?: boolean
  text?: string
  hasSearchbar?: boolean
  onExportClick?: () => void
  onSearchChange?: (val: string) => void
  pageControlled?: number
  onPageChange?: (page: number) => void
  styleText?: any
  icon?: string
  actions?: ActionProps[]
  expandedRowRender?: any
  borderRadius?: number
  headerBgColor?: string
  onExpand?: (rowData: any) => void
  setId?: (val: string) => void
  onSave?: (data: any) => void
  hasSaveButton?: boolean
  onRowClick?: (data: any) => void
  loading?: boolean
  clientSearch?: boolean
  savedTitle?: string
}

const Table: React.FC<TableProps> = ({
  title,
  columns = [],
  data = [],
  totalPages,
  totalItems,
  itemsPerPage,
  hasExportButton = false,
  text,
  searchKeys = ['email', 'name', 'phone'],
  hasSearchbar = true,
  onExportClick,
  onSearchChange,
  pageControlled,
  onPageChange,
  styleText,
  icon,
  actions,
  expandedRowRender,
  headerBgColor,
  borderRadius,
  setId,
  onSave,
  hasSaveButton,
  onRowClick,
  loading,
  clientSearch = false,
  savedTitle,
}) => {
  const tableRef = useRef<HTMLDivElement>(null)
  const handleDownloadPDF = async () => {
    if (!tableRef.current) return
    const tableEl = tableRef.current

    const originalOverflow = tableEl.style.overflow
    const originalOverflowX = tableEl.style.overflowX
    const originalWidth = tableEl.style.width

    tableEl.style.overflow = 'visible'
    tableEl.style.overflowX = 'visible'
    tableEl.style.width = tableEl.scrollWidth + 'px'

    const canvas = await html2canvas(tableEl, {
      scale: 2,
      useCORS: true,
      scrollX: 0,
      scrollY: -window.scrollY,
    })

    const imgData = canvas.toDataURL('image/png')
    const pdf = new jsPDF('portrait', 'px', 'a4')

    const pageWidth = pdf.internal.pageSize.getWidth()
    const pageHeight = pdf.internal.pageSize.getHeight()

    const imgWidth = pageWidth
    const imgHeight = (canvas.height * imgWidth) / canvas.width

    const currentDateTime = new Date().toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })

    const marginTop = 30
    let heightLeft = imgHeight
    let position = 0

    while (heightLeft > 0) {
      pdf.setFontSize(10)
      pdf.text(currentDateTime, 10, 20)

      pdf.addImage(imgData, 'PNG', 0, marginTop + position, imgWidth, imgHeight)

      heightLeft -= pageHeight - marginTop
      position -= pageHeight - marginTop
      if (heightLeft > 0) pdf.addPage()
    }

    pdf.save(savedTitle || 'table.pdf')

    tableEl.style.overflow = originalOverflow
    tableEl.style.overflowX = originalOverflowX
    tableEl.style.width = originalWidth
  }
  const [pageInternal, setPageInternal] = useState<number>(1)
  const [searchInput, setSearchInput] = useState('')

  const page = pageControlled ?? pageInternal
  const setPage = onPageChange ?? setPageInternal

  const debouncedValue = useDebounce(searchInput, 200)

  useEffect(() => {
    onSearchChange?.(debouncedValue)
  }, [debouncedValue, onSearchChange])

  const dummyColumns = [
    {
      title: 'SN',
      key: 'sn',
      render: (_item: any, index: number) => index + 1,
    },
    {
      title: 'Name',
      key: 'name',
    },
    {
      title: 'Email',
      key: 'email',
    },
    {
      title: 'Age',
      key: 'age',
    },
  ]

  const dummyData = []

  const actualData = data?.length ? data : dummyData
  const actualColumns = columns?.length ? columns : dummyColumns

  const filteredData = actualData.filter((d) =>
    searchKeys.some((key) =>
      d[key]
        ?.toString()
        .toLowerCase()
        .trim()
        .includes(debouncedValue.toLowerCase().trim()),
    ),
  )

  return (
    <>
      <S.StyleTableWrapper borderRadius={borderRadius}>
        <S.StyleTableTitle>{title}</S.StyleTableTitle>
        {!!hasSearchbar && (
          <SearchInput
            text={text}
            hasButton={hasExportButton}
            onButtonClick={handleDownloadPDF}
            searchInput={searchInput}
            setSearchInput={setSearchInput}
            placeholder={searchKeys}
            styleText={styleText}
            icon={icon}
          />
        )}

        <S.StyleTableResponsiveWrapper>
          <div ref={tableRef}>
            <S.StyleTable>
              <Head
                columns={actualColumns}
                actions={actions}
                hasExpandable={!!expandedRowRender}
                headerBgColor={headerBgColor}
              />
              <Body
                loading={loading}
                columns={actualColumns}
                data={hasSearchbar && clientSearch ? filteredData : actualData}
                actions={actions}
                expandedRowRender={expandedRowRender}
                setId={setId}
                onSave={onSave}
                hasSaveButton={hasSaveButton}
                onRowClick={onRowClick}
              />
            </S.StyleTable>
          </div>
        </S.StyleTableResponsiveWrapper>
      </S.StyleTableWrapper>
      <Pagination
        isShown={totalItems > itemsPerPage}
        currentPage={page}
        totalPages={totalPages}
        itemsPerpage={itemsPerPage}
        setPage={setPage}
        totalItems={totalItems}
      />
    </>
  )
}

export default Table
